import { useState, useEffect } from 'react';
import { auditAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DataTable, SortableHeader } from '../../components/ui/data-table';
import { Loader2, Search, RefreshCcw } from 'lucide-react';
import { formatIndianCurrency } from '../../lib/utils';

const actionOptions = [
  'LOGIN_SUCCESS', 'LOGIN_FAILURE', 'LOGOUT', 'PASSWORD_CHANGED', 'PASSWORD_RESET', 'PASSWORD_RESET_BY_ADMIN', 'TOKEN_REFRESH',
  'EMPLOYEE_CREATED', 'EMPLOYEE_UPDATED', 'EMPLOYEE_DELETED', 'EMPLOYEE_STATUS_CHANGED', 'EMPLOYEE_BULK_UPLOAD', 'PROFILE_PICTURE_UPDATED',
  'SALARY_STRUCTURE_CREATED', 'SALARY_STRUCTURE_UPDATED', 'SALARY_INCREMENT_PROCESSED',
  'PAYROLL_RUN_CREATED', 'PAYROLL_RUN_LOCKED', 'PAYROLL_RUN_DELETED', 'PAYROLL_TAX_OVERRIDDEN', 'PAYROLL_ARREARS_APPLIED', 'PAYROLL_ADHOC_ADDED', 'PAYROLL_ADHOC_REMOVED',
  'COMPANY_CREATED', 'COMPANY_UPDATED', 'COMPANY_PURGED', 'COMPANY_SETTINGS_UPDATED', 'COMPANY_MODULES_UPDATED', 'COMPANY_LOGO_UPDATED',
  'USER_CREATED', 'USER_UPDATED', 'USER_DELETED', 'ROLE_CREATED', 'ROLE_UPDATED', 'ROLE_DELETED',
  'LEAVE_APPROVED', 'LEAVE_REJECTED', 'LEAVE_CANCELLED',
  'ATTENDANCE_REGULARIZATION_REQUESTED', 'ATTENDANCE_REGULARIZED', 'ATTENDANCE_BULK_ACTION', 'ATTENDANCE_CONFIG_UPDATED', 'ATTENDANCE_CONFIG_DELETED',
  'ASSET_CREATED', 'ASSET_UPDATED', 'ASSET_DELETED', 'ASSET_ASSIGNED', 'ASSET_UNASSIGNED',
];

const entityOptions = ['EMPLOYEE', 'SALARY', 'PAYROLL', 'COMPANY', 'USER', 'ROLE', 'LEAVE', 'ATTENDANCE', 'ATTENDANCE_CONFIG', 'ASSET'];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    start_date: '',
    end_date: '',
  });

  const fetchLogs = async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum, page_size: pageSize };
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.start_date) params.start_date = `${filters.start_date}T00:00:00`;
      if (filters.end_date) params.end_date = `${filters.end_date}T23:59:59`;

      const res = await auditAPI.getLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.action, filters.entity_type, filters.user_id, filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') return formatIndianCurrency(val);
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const formatChanges = (oldValues, newValues) => {
    if (!oldValues && !newValues) return '-';
    const keys = new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]);
    const parts = [];
    keys.forEach(k => {
      const oldVal = oldValues?.[k];
      const newVal = newValues?.[k];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        parts.push(`${k}: ${formatValue(oldVal)} → ${formatValue(newVal)}`);
      }
    });
    return parts.length ? parts.join(', ') : '-';
  };

  const columns = [
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column} title="Timestamp" />,
      cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
    },
    {
      accessorKey: 'user_email',
      header: ({ column }) => <SortableHeader column={column} title="User" />,
      cell: ({ row }) => row.original.user_email || row.original.performed_by_name || row.original.user_id || 'System',
    },
    {
      accessorKey: 'action',
      header: ({ column }) => <SortableHeader column={column} title="Action" />,
    },
    {
      accessorKey: 'entity_type',
      header: ({ column }) => <SortableHeader column={column} title="Entity" />,
      cell: ({ row }) => row.original.entity_type ? `${row.original.entity_type}${row.original.entity_id ? ` (${row.original.entity_id.slice(-6)})` : ''}` : '-',
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} title="Status" />,
      cell: ({ row }) => (
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${row.original.status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      accessorKey: 'changes',
      header: 'Changes',
      cell: ({ row }) => (
        <div className="max-w-md truncate text-xs text-muted-foreground" title={formatChanges(row.original.old_values, row.original.new_values)}>
          {formatChanges(row.original.old_values, row.original.new_values)}
        </div>
      ),
    },
    {
      accessorKey: 'note',
      header: 'Note',
      cell: ({ row }) => <div className="max-w-xs truncate text-xs text-muted-foreground">{row.original.note || '-'}</div>,
    },
  ];

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Track who did what and when across the system</p>
        </div>
        <Button variant="outline" onClick={() => fetchLogs(page)} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">All actions</option>
                {actionOptions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Entity</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              >
                <option value="">All entities</option>
                {entityOptions.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>User ID / Email</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Filter by user"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTable columns={columns} data={logs} loading={loading} emptyMessage="No audit logs found" />
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total} logs
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
